// Three-tile header above the meetings list (Overdue / Upcoming this
// week / Held on time). Reuses the deliverable MetricCard primitive so
// the visual rhythm of summary tiles stays consistent across tracking
// surfaces. Recurring parents and archived rows are excluded from every
// count — parents are schedule templates, not actionable items, and
// archived rows are historical. Hides itself entirely when there are no
// meetings on the contract (empty-state).
//
// Invariant: when the on-time sample is below the minimum
// threshold (sample < 3) the value renders "—" AND the subtitle names
// the sample size honestly ("not enough data yet (N past meeting{s})").
// Don't drop the caption — small samples must be reported as such, not
// as a fake 0% / 100%.

import { useState } from "react";
import { Info } from "lucide-react";
import MetricCard from "../deliverable/MetricCard";
import { calculateOnTimeRate } from "./calculateOnTimeRate";
import {
  meetingGraceDays,
  type MeetingGraceConfig,
} from "./meetingGraceDays";
import type { Meeting, RecurrenceRule } from "../../../../types";

type GraceOverride = number | MeetingGraceConfig | null | undefined;

// Build the full grace-window breakdown for the panel. Lists every
// cadence the user can configure (Daily / Weekly / Fortnightly /
// Monthly / Quarterly / Annual / One-off) with the effective grace
// in days, computed via the same helper the on-time rate uses. Drives
// the "ⓘ within grace of scheduled" expandable info panel.
interface GraceBreakdownRow {
  label: string;
  days: number;
}

interface GraceBreakdown {
  rows: GraceBreakdownRow[];
  /** "default" / "single" / "per-frequency" — drives the explanatory
   *  one-liner above the row list. */
  mode: "default" | "single" | "per-frequency";
  /** Single-mode value, when mode === 'single'. Used to render the
   *  "no grace" copy at 0. */
  singleValue?: number;
}

function makeRule(
  frequency: RecurrenceRule["frequency"],
  interval = 1,
): RecurrenceRule {
  return { frequency, interval, startDate: "2000-01-01" };
}

function buildGraceBreakdown(
  graceOverride?: GraceOverride,
): GraceBreakdown {
  const hasOverrideObj =
    graceOverride && typeof graceOverride === "object";
  const single =
    typeof graceOverride === "number"
      ? graceOverride
      : hasOverrideObj
        ? graceOverride.graceDays
        : undefined;
  const perFreq = hasOverrideObj
    ? graceOverride.graceDaysByFrequency
    : undefined;
  const hasPerFreq =
    !!perFreq && Object.values(perFreq).some((v) => typeof v === "number");
  const hasSingle =
    typeof single === "number" && Number.isFinite(single);

  const mode: GraceBreakdown["mode"] = hasPerFreq
    ? "per-frequency"
    : hasSingle
      ? "single"
      : "default";

  // Resolve the effective grace for each cadence row by calling the
  // same helper the on-time rate uses, threading the override config.
  const config: MeetingGraceConfig | undefined = hasOverrideObj
    ? graceOverride
    : typeof graceOverride === "number"
      ? { graceDays: graceOverride }
      : undefined;

  const rows: GraceBreakdownRow[] = [
    { label: "Daily", days: meetingGraceDays(makeRule("daily"), config) },
    { label: "Weekly", days: meetingGraceDays(makeRule("weekly"), config) },
    {
      label: "Fortnightly",
      days: meetingGraceDays(makeRule("weekly", 2), config),
    },
    { label: "Monthly", days: meetingGraceDays(makeRule("monthly"), config) },
    {
      label: "Quarterly",
      days: meetingGraceDays(makeRule("quarterly"), config),
    },
    {
      label: "Annual",
      days: meetingGraceDays(makeRule("annually"), config),
    },
    { label: "One-off", days: meetingGraceDays(undefined, config) },
  ];

  return {
    rows,
    mode,
    singleValue: hasSingle ? single : undefined,
  };
}

export interface MeetingSummaryStripProps {
  meetings: Meeting[];
  today?: Date;
  /** Per-contract override for the on-time grace window. Either a
   *  single number (single-mode), a config object with per-frequency
   *  overrides, or null/undefined to use the cadence-proportional
   *  formula. The tooltip on "within grace of scheduled" reflects
   *  whichever rule applies. */
  graceOverride?: GraceOverride;
}

const DAY_MS = 86_400_000;

function startOfUTCDay(d: Date): number {
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

function isoToUTCDay(iso: string): number | null {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

function dayName(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-AU", {
    weekday: "long",
    timeZone: "UTC",
  });
}

function liveScheduledMeetings(meetings: Meeting[]): Meeting[] {
  return meetings.filter(
    (m) => !m.archivedAt && !m.recurrenceRule && m.status === "scheduled",
  );
}

export default function MeetingSummaryStrip({
  meetings,
  today = new Date(),
  graceOverride,
}: MeetingSummaryStripProps) {
  const [graceInfoOpen, setGraceInfoOpen] = useState(false);
  if (meetings.length === 0) return null;

  const todayMs = startOfUTCDay(today);
  const sevenDaysMs = todayMs + 7 * DAY_MS;

  const live = liveScheduledMeetings(meetings);

  const overdueCount = live.filter((m) => {
    const day = isoToUTCDay(m.date);
    return day !== null && day < todayMs;
  }).length;

  const upcomingItems = live
    .filter((m) => {
      const day = isoToUTCDay(m.date);
      return day !== null && day >= todayMs && day <= sevenDaysMs;
    })
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  const upcomingCount = upcomingItems.length;
  const earliest = upcomingItems[0];

  // On-time sample is built from past held meetings only — see
  // calculateOnTimeRate. The "past meetings" caption denominator
  // counts past non-cancelled rows so the user sees the size of the
  // raw event window, not just the held subset. Recurring parents
  // are NOT excluded here because the calculator itself does not
  // filter them — keep the two counts in lockstep.
  const pastNonCancelled = meetings.filter(
    (m) => !m.archivedAt && m.status !== "cancelled",
  ).filter((m) => {
    const day = isoToUTCDay(m.date);
    return day !== null && day < todayMs;
  });
  const pastCount = pastNonCancelled.length;
  const heldRate = calculateOnTimeRate(meetings, today, graceOverride);

  const breakdown = buildGraceBreakdown(graceOverride);

  let heldValue: string;
  let heldValueClass: string;
  let heldSubtitle: React.ReactNode;
  if (heldRate.pct === null) {
    heldValue = "—";
    heldValueClass = "text-slate-900 dark:text-slate-100";
    const noun = pastCount === 1 ? "meeting" : "meetings";
    heldSubtitle = `not enough data yet (${pastCount} past ${noun})`;
  } else {
    heldValue = `${heldRate.pct}%`;
    if (heldRate.pct >= 90)
      heldValueClass = "text-emerald-600 dark:text-emerald-400";
    else if (heldRate.pct >= 70)
      heldValueClass = "text-slate-900 dark:text-slate-100";
    else heldValueClass = "text-amber-600 dark:text-amber-400";
    const noun = heldRate.sampleSize === 1 ? "meeting" : "meetings";
    heldSubtitle = `${heldRate.numerator} of ${heldRate.sampleSize} past ${noun} · within grace of scheduled`;
  }

  // The Held on time tile bypasses MetricCard so it can render an
  // info button + collapsible breakdown panel inline. Mirrors the
  // milestone Value-completed pattern so the two surfaces feel
  // consistent.
  const ruleLine =
    breakdown.mode === "single"
      ? breakdown.singleValue === 0
        ? "Held on the scheduled date counts as on-time (no grace, per contract setting)."
        : `Held within ${breakdown.singleValue} day${breakdown.singleValue === 1 ? "" : "s"} of the scheduled date counts as on-time, per contract setting.`
      : breakdown.mode === "per-frequency"
        ? "Held within the per-cadence window set on this contract counts as on-time. Unset cadences fall back to the cadence-proportional default."
        : "Held within the cadence-proportional window counts as on-time.";

  return (
    <div
      data-testid="meeting-summary-strip"
      className="grid grid-cols-3 gap-3"
    >
      <MetricCard
        testId="metric-card-overdue"
        label="Overdue"
        value={overdueCount}
        valueTone={overdueCount > 0 ? "danger" : "neutral"}
      />
      <MetricCard
        testId="metric-card-upcoming-this-week"
        label="Upcoming this week"
        value={upcomingCount}
        valueTone={upcomingCount > 0 ? "brandStrong" : "neutral"}
        subtitle={
          earliest
            ? `next: ${earliest.name} · ${dayName(earliest.date)}`
            : "nothing scheduled this week"
        }
      />

      <div
        data-testid="metric-card-held-on-time"
        className="rounded-md border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800/60"
      >
        <div className="flex items-center gap-1.5">
          <span className="text-caption font-medium text-slate-500 dark:text-slate-400">
            Held on time
          </span>
          <button
            type="button"
            onClick={() => setGraceInfoOpen((v) => !v)}
            aria-label="Held on time — grace window info"
            aria-expanded={graceInfoOpen}
            data-testid="held-on-time-info-toggle"
            className="inline-flex h-4 w-4 items-center justify-center rounded text-slate-500 hover:text-slate-700 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:text-slate-400 dark:hover:text-slate-200"
          >
            <Info className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </div>
        <div
          data-testid="metric-card-held-on-time-value"
          className={`mt-1 text-h2 font-semibold ${heldValueClass}`}
        >
          {heldValue}
        </div>
        <div
          data-testid="metric-card-held-on-time-subtitle"
          className="mt-1 text-caption text-slate-500 dark:text-slate-400"
        >
          {heldSubtitle}
        </div>
        {graceInfoOpen && (
          <div
            data-testid="held-on-time-info-panel"
            className="mt-2 rounded-md bg-slate-50 p-3 text-caption text-slate-700 dark:bg-slate-900 dark:text-slate-200"
          >
            <p>{ruleLine}</p>
            <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1">
              {breakdown.rows.map((r) => (
                <div key={r.label} className="flex justify-between">
                  <dt className="text-slate-600 dark:text-slate-300">
                    {r.label}
                  </dt>
                  <dd className="font-medium text-slate-900 dark:text-slate-100">
                    {r.days}d
                  </dd>
                </div>
              ))}
            </dl>
            <p className="mt-2 text-slate-500 dark:text-slate-400">
              Configure via Edit contract → Contract settings.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
